<template>
	<div class="product-accounts-wrapper">
		<!-- Product wrapper with name and total amount + small chart -->
		<div class="product-wrapper" @click="showAccounts = !showAccounts">
			<icon-arrow-down :class="{ 'rotate': showAccounts}"/>
			<div class="product-name">{{product.description}}</div>
			<div class="product-amount" :class="{ 'hide': showAccounts }">
                <div class="icon" :class="{'negative': product.totalAmount < 0}"><component :is="amountIcon(product.totalAmount)"/></div>
                <span>{{amountFormat(Math.abs(product.totalAmount), currencyParser(currency))}}</span>
            </div>
			<div class="chart-container" :class="{ 'hide': showAccounts }">
				<div class="percentage-label">{{product.percentage !== 0 && product.parsedPercentage === 0 ? '≈ ' : ''}}{{product.parsedPercentage}}%</div>
				<chart-simple
				:chartId="chartIdConstructor"
				:graphData="graphData"
				:colorsArray="colorsArray"
				:pieHole="0.8"/>
			</div>
		</div>
		<transition name="show-products" mode="out-in">
			<div class="detail-product-accounts" v-if="showAccounts" :style="productAccountsCss">
                <!-- Table -->
                <table-generic
                :headerData="tableHeader"
                :tableStyles="tableStyles"
                :tableData="tableData"
                @rowClicked="accountClickHandler"/>
				<!-- Sub-total of product -->
				<div class="products-total-wrapper">
					<div class="label">{{$t('accounts.patrimony.labels.subTotal')}}</div>
					<div class="amount">
                        <div class="icon" :class="{'negative': product.availableTotalAmount < 0}"><component :is="amountIcon(product.availableTotalAmount)"/></div>
                        <span>{{product.availableTotalAmount ? amountFormat(Math.abs(product.availableTotalAmount), currencyParser(currency)) : '-'}}</span>
					</div>
                    <!-- Product Total Amount -->
					<div class="amount balance">
                        <div class="icon" :class="{'negative': product.totalAmount < 0}"><component :is="amountIcon(product.totalAmount)"/></div>
                        <span>{{amountFormat(Math.abs(product.totalAmount), currencyParser(currency))}}</span>
                    </div>
                    <!-- Product % -->
					<div class="percentage">{{product.percentage !== 0 && product.parsedPercentage === 0 ? '≈ ' : ''}}{{product.parsedPercentage}}%</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup>
import ChartSimple from '@/components/generic/charts/Simple'
import IconArrowDown from '@/assets/icons/arrow-down.svg?inline'
import TableGeneric from '@/components/generic/table/Generic'
/* Vue & Assets */
import { ref, computed, defineAsyncComponent } from 'vue'
import { calculateAccountPercentage } from '@/assets/scripts/parsers/patrimony'
import { amountFormat, currencyParser } from '@/assets/scripts/amounts'
import { accountFormatHelper } from '@/assets/scripts/strings'
import { getAccountRouteName } from '@/assets/scripts/mappers/accounts'
import { navigateByName } from '@/router/helpers/navigation'
import { PATRIMONY_COLORS, PATRIMONY_COLORS_DARK } from '@/assets/constants/core'
import { WALLET, ACCOUNTS_NIB_IBAN } from '@/assets/constants/masks'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** props received
@currency {string} - selected currency in our picker
@product {Object} - selected product
@productsList {Array} - list of products
@accountFinancialType {String} - type of financial family account
@savedBackState {Object} - saved back state
*/
const props = defineProps({
    currency: String,
    product: Object,
    productsList: Array,
    accountFinancialType: String,
    savedBackState: Object
})

const { savePatrimonyBackState } = mapActions(moduleMapper.ACCOUNTS_PATRIMONY)
const { getPatrimonyAccountsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAppThemeMode } = mapGetters()

const amountIcon = amount => {
	if (!amount || amount === 0) return null
	const icon = amount > 0 ? 'plus' : 'minus'
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}

const graphData = ref([])
const colorsArray = ref([])
const notSelectedColor = ref('#ebf1f3')
const showAccounts = ref(false)

const graphDataConstructor = () => {
    if (!props.product.totalAmount) {
        graphData.value = [['noAmount', 100]]
        colorsArray.value = [notSelectedColor.value]
    }
    props.productsList.forEach((product, i) => {
        graphData.value.push([
            product.id.replace(/ /g, ''),
            parseInt(product.percentage < 1 && product.percentage !== 0 ? 1 : product.percentage)
        ])
        colorsArray.value.push(product.description === props.product.description ? (getAppThemeMode.value === 'dark' ? PATRIMONY_COLORS_DARK[i] : PATRIMONY_COLORS[i]) : notSelectedColor.value)
    })
}

// Table
const tableHeader = ref([
    { label: t('accounts.patrimony.accountsTable.accountNumber'), dataAttr: 'accountNumber' },
    { label: t('accounts.patrimony.accountsTable.accountName'), dataAttr: 'description' },
    { label: t('accounts.patrimony.accountsTable.availableBalance'), dataAttr: 'availableBalance' },
    { label: t('accounts.patrimony.accountsTable.balance'), dataAttr: 'balance' },
    { label: t('accounts.patrimony.accountsTable.percentage'), dataAttr: 'percentage' }
])

const tableStyles = [
    { width: '110px', 'min-width': '110px' },
    { width: '20%', 'min-width': '20%', 'text-align': 'left' },
    { width: '20%', 'min-width': '20%', 'text-align': 'right' },
    { width: '20%', 'min-width': '20%', 'text-align': 'right' },
    { width: '50px', 'min-width': '50px', 'text-align': 'right' }
]

const tableData = computed(() => {
    return getPatrimonyAccountsList.value(props.currency, props.product.id, props.accountFinancialType).map(account => {
        return 	[
            { value: account.id, dataAttr: 'id' },
            {
                type: 'title',
                value: accountFormatHelper(account.accountNumber),
                dataAttr: 'accountNumber'
            },
            { value: account.accountDescription || '-', dataAttr: 'description' },
            {
                type: account.availableBalance ? 'amount' : 'description',
                value: account.availableBalance ? { content: account.availableBalance, currency: account.accountCurrency } : '-',
                dataAttr: 'availableBalance'
            },
            {
                type: 'amount',
                value: { content: account.balance, currency: account.accountCurrency },
                dataAttr: 'balance'
            },
            { value: `${calculateAccountPercentage(account.balance, props.product.totalAmount)}%`, dataAttr: 'percentage' }
        ]
    })
})

const productAccountsCss = computed(() => {
    return {
        '--height': `${(tableData.value.length * 72) + 30 + 72}px` // this is the height for each row + row header + padding + subtotal
    }
})

const chartIdConstructor = computed(() => {
    return `${props.currency}${props.accountFinancialType}${props.product.id.replace(/ /g, '')}Chart`
})

const accountClickHandler = account => {
    const tempAccount = getPatrimonyAccountsList.value().find(obj => obj.id === account.rowId)
    navigateByName(getAccountRouteName(tempAccount.accountType), { tempAccount: account.rowId })
    savePatrimonyBackState(props.savedBackState)
}

graphDataConstructor()
</script>

<style lang="scss" scoped>
.product-accounts-wrapper {
    width: 100%;
    height: auto;
    overflow: hidden;

    display: flex;
    flex-direction: column;

    margin-bottom: 8px;
    border-radius: 8px;
    border: solid 1px var(--secondary-light-1);
    cursor: pointer;

    .product-wrapper {
        width: 100%;
        height: 56px;
        padding: 0 16px;
        overflow: hidden;

        display: flex;
        flex-direction: row;
        align-items: center;

        svg {
            flex-shrink: 0;

            margin-right: 8px;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-4)
            }

            &.rotate{
                transform: rotate(180deg);
            }
        }

        .product-name {
            width: 100%;

            @include H7-Medium;
            color: var(--secondary-dark-2);
        }

        .product-amount {
            padding: 0 24px;
            display: flex;
            flex-shrink: 0;
            justify-content: flex-end;
            gap: 4px;

            @include H8-Regular;
            color: var(--secondary-dark-1);

            .icon {
                width: 8px;
                height: 8px;

                flex-shrink: 0;

                svg {
                    width: 100%;
                    height: 100%;

                    :deep(.change-color-fill) {
                        fill: var(--positive);
                    }
                }

                &.negative {
                    svg {
                        :deep(.change-color-fill) {
                            fill: var(--negative);
                        }
                    }
                }
            }
        }

        .chart-container {
            width: 50px;
            height: 50px;
            position: relative;
            flex-shrink: 0;

            .percentage-label {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                @include H9-Regular;
                color: var(--secondary-dark-2);

                z-index: 1;
            }
        }

        .product-amount , .chart-container {
            transition: opacity 0.2s ease-in-out;

            &.hide {
                opacity: 0;

            }
        }
    }

    .detail-product-accounts {
        display: flex;
        flex-direction: column;
        align-items: center;

        overflow: hidden;
        transition: all 0.4s ease-in-out;
        overflow: hidden;

        &.show-products-enter-active,
        &.show-products-leave-active {
            transition: all 0.2s linear;
        }

        &.show-products-enter-from,
        &.show-products-leave-to {
            height: 0;
            transition: all 0.2s linear;
        }

        &.hide {
            height: 0;
            transition: all 0.3s ease-in-out;
        }

        .table-bed {
            box-shadow: unset;

            :deep(.data-table-wrapper) {
                .table-header-wrapper {
                    .table-header-cell {
                        border: none;
                    }
                }

                .data-table-row {
                    &:last-of-type {
                        td {
                            border-bottom: none;
                            border-radius: 0;
                        }
                    }

                    td {
                        border-left: none;
                        border-right: none;
                    }

                    .value {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }
            }
        }

        .products-total-wrapper {
            width: calc(100% - 32px);
            height: 32px;
            margin: 16px 0;
            padding-left: 16px;
            border-radius: 8px;

            display: flex;
            flex-direction: row;
            align-items: center;

            @include H8-Medium;
            color: var(--secondary-dark-2);
            background-color: var(--secondary-light-3);

            .label {
                width: 40%;
                min-width: 40%;
            }
            .amount {
                width: 20%;
                min-width: 20%;

                display: flex;
                flex-shrink: 0;
                justify-content: flex-end;
                align-items: center;

                text-align: right;
                overflow: hidden;

                &:not(.balance) {
                    padding-right: 8px;
                }

                .icon {
                    width: 12px;
                    height: 12px;

                    flex-shrink: 0;

                    svg {
                        width: 100%;
                        height: 100%;

                        :deep(.change-color-fill) {
                            fill: var(--positive);
                        }
                    }

                    &.negative {
                        svg {
                            :deep(.change-color-fill) {
                                fill: var(--negative);
                            }
                        }
                    }
                }

                span {
                    overflow:hidden;
                    text-overflow:ellipsis;
                    white-space:nowrap;
                }
            }
            .percentage {
                width: 9.5%;
                flex-grow: 0;
                flex-shrink: 0;
                text-align: right;
            }
        }
    }
}
</style>
